/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Ladda from 'ladda';
import Radio from 'backbone.radio';

import showErrorMessage from '@/util/showErrorMessage';
import GetAllAWPackageStatuses from '@/services/GetAllAWPackageStatuses';
import lazyLoadView from '@/util/lazyLoadView';

import SearchAWPackages from '../../services/SearchAWPackages';
import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import IntDashboardSidebarNavView from '../../../internal-dashboard/views/IntDashboardSidebarNavView';
import AWPackageDashboardPageGridView from '../../views/AWPackageDashboardGridView';
import GetAllOrderFormTypes from '../../../common/services/GetAllOrderFormTypes';
import CreateAWPackage from '../../services/CreateAWPackage';
import GetAllRegionGroups from '../../../common/services/GetAllRegionGroups';

import tmplAWPackageDashboardPage from './AWPackageDashboardPage.hbs';

const AWPackageDashboardPage = View.extend({
  template: tmplAWPackageDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    searchFieldsView: '.search-fields-view',
    awPackageDashboardGridView: '.aw-package-dashboard-grid-view',
    btnCreateAWPackage: '.btn-create-aw-package'
  },

  regions: {
    sideBar: '@ui.sideBar',
    searchFieldsView: '@ui.searchFieldsView',
    awPackageDashboardGridView: '@ui.awPackageDashboardGridView'
  },

  events: {
    'click @ui.btnCreateAWPackage': 'onClickCreateAWPackage'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  onSearchResponse(response) {
    this.$('.result-count').empty().append(`<span><b>Total Result Count: ${response.totalCount}</b></span>`);
    this.awPackageDashboardGridView.collection.fullCollection.reset(response.awPackages);
  },

  onRender() {
    this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'aw-packages' }));

    const searchFields = [
      {
        id: 'aw-package-id-search-field',
        name: 'awPackageId',
        class: 'InputSearchField',
        placeholder: 'AW Package ID'
      },
      {
        id: 'command-name-search-field',
        name: 'commandName',
        class: 'InputSearchField',
        placeholder: 'Command Name'
      },
      {
        id: 'aw-type-search-field',
        name: 'typeID',
        class: 'SelectSearchField',
        placeholder: 'Type',
        lookupService: GetAllOrderFormTypes,
        lookupMapping: (type) => {
          const { formName, id } = type;
          return { optionText: formName, id };
        }
      },
      {
        id: 'aw-status-search-field',
        name: 'statusID',
        class: 'SelectSearchField',
        placeholder: 'Status',
        lookupService: GetAllAWPackageStatuses,
        lookupMapping: (status) => {
          const { awPackageStatus, id } = status;
          return { optionText: awPackageStatus, id };
        }
      },
      {
        id: 'region-group-search-field',
        name: 'regionGroupID',
        class: 'SelectSearchField',
        placeholder: 'Region Group',
        lookupService: GetAllRegionGroups,
        lookupMapping: (regionGroups) => {
          const { regionGroup, id } = regionGroups;
          return { optionText: regionGroup, id };
        }
      },
      {
        id: 'date-created-start-search-field',
        name: 'dateCreatedStart',
        class: 'DateSearchField',
        placeholder: 'Created On Start'
      },
      {
        id: 'date-created-end-search-field',
        name: 'dateCreatedEnd',
        class: 'DateSearchField',
        placeholder: 'Created On End'
      },
      {
        id: 'pop-start-date-start-search-field',
        name: 'popStartDateStart',
        class: 'DateSearchField',
        placeholder: 'POP Start Date Start'
      },
      {
        id: 'pop-start-date-end-search-field',
        name: 'popStartDateEnd',
        class: 'DateSearchField',
        placeholder: 'POP Start Date End'
      }
    ];

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchAWPackages))
    );

    this.awPackageDashboardGridView = new AWPackageDashboardPageGridView({
      pageSize: 10,
      model: new Backbone.Model({})
    });
    this.showChildView('awPackageDashboardGridView', this.awPackageDashboardGridView);
  },

  showAWPackageCreationError() {
    showErrorMessage({
      errorCode: 'awPackageCreationError',
      errorTitle: 'Creation Error',
      errorMessage:
        'An error occurred while creating the AW Package. Try again or contact an administrator if your issue persists.'
    });
  },

  async onClickCreateAWPackage(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnCreateAWPackage[0]);
    spinner.start();

    try {
      const newAWPackage = await CreateAWPackage();
      Radio.channel('navigate').request('show:int:aw-package', newAWPackage.awPackageId);
    } catch (err) {
      this.showAWPackageCreationError();
    } finally {
      spinner.stop();
    }
  }
});

export default AWPackageDashboardPage;
